import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import useStore from '../config/store';
import DiscComparisonResults from './DiscComparisonResults';

const DiscDashboardComparison = () => {
  const { t } = useTranslation();
  const project = useStore((state) => state.project);
  const { authToken, showAlert, user } = useStore.getState();

  const [comparisons, setComparisons] = useState([]);
  const [loadingComparisons, setLoadingComparisons] = useState(false);
  const [errorComparisons, setErrorComparisons] = useState(null);
  const [deleteComparisonDialogOpen, setDeleteComparisonDialogOpen] = useState(false);
  const [comparisonToDelete, setComparisonToDelete] = useState(null);
  const [deletingComparison, setDeletingComparison] = useState(false);
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [selectedComparison, setSelectedComparison] = useState(null);
  // Reproceso: solo super-admin (no cobra puntos)
  const [comparisonToReprocess, setComparisonToReprocess] = useState(null);
  const [reprocessingId, setReprocessingId] = useState(null);
  const isSuperAdmin = user?.rol === 'super-admin';

  // silent: refresco en segundo plano, sin spinner ni mensaje de error que reemplace la tabla
  const fetchComparisons = async ({ silent = false } = {}) => {
    if (!project) {
      setLoadingComparisons(false);
      setComparisons([]);
      return;
    }

    if (!silent) {
      setLoadingComparisons(true);
      setErrorComparisons(null);
    }

    try {
      const response = await api.get(
        '/disc-comparisons/by-project',
        {
          params: { projectId: project },
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.errorCode === 0) {
        setComparisons(response.data.response.data || []);
      } else if (!silent) {
        setErrorComparisons(response.data.errorMessage);
      }
    } catch (err) {
      console.error('Error fetching comparisons:', err);
      if (!silent) setErrorComparisons('Error al cargar las comparaciones');
    } finally {
      if (!silent) setLoadingComparisons(false);
    }
  };

  useEffect(() => {
    fetchComparisons();
  }, [project]);

  // Mientras haya comparaciones procesando (por ejemplo, tras reprocesar), refresca la tabla en silencio cada 15 s
  const fetchComparisonsRef = useRef(fetchComparisons);
  fetchComparisonsRef.current = fetchComparisons;
  const hasProcessing = comparisons.some((comparison) => comparison.status === 'procesando');

  useEffect(() => {
    if (!hasProcessing) return undefined;
    const timer = setInterval(() => fetchComparisonsRef.current({ silent: true }), 15000);
    return () => clearInterval(timer);
  }, [hasProcessing]);

  const handleViewComparison = (comparison) => {
    setSelectedComparison(comparison);
    setComparisonModalOpen(true);
  };

  const handleOpenDeleteComparisonDialog = (comparison) => {
    setComparisonToDelete(comparison);
    setDeleteComparisonDialogOpen(true);
  };

  const handleCloseDeleteComparisonDialog = () => {
    setDeleteComparisonDialogOpen(false);
    setComparisonToDelete(null);
  };

  const handleConfirmDeleteComparison = async () => {
    if (!comparisonToDelete) return;

    setDeletingComparison(true);
    try {
      const response = await api.delete(
        `/disc-comparisons/${comparisonToDelete.id}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.errorCode === 0) {
        showAlert('Comparación eliminada exitosamente', 'success');
        fetchComparisons();
      } else {
        showAlert(response.data.errorMessage || 'Error al eliminar comparación', 'error');
      }
    } catch (err) {
      console.error('Error deleting comparison:', err);
      showAlert('Error al eliminar comparación', 'error');
    } finally {
      setDeletingComparison(false);
      handleCloseDeleteComparisonDialog();
    }
  };

  const handleCloseReprocessDialog = () => {
    setComparisonToReprocess(null);
  };

  // Reprocesar: vuelve a generar el análisis con IA y su PDF (solo super-admin; no cobra puntos)
  const handleConfirmReprocess = async () => {
    if (!comparisonToReprocess) return;
    const { id } = comparisonToReprocess;
    setComparisonToReprocess(null);
    setReprocessingId(id);

    try {
      const response = await api.post(
        `/disc-comparisons/${id}/reprocess`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.errorCode === 0) {
        showAlert(t('disc.discComparisons.reprocessStarted'), 'success');
        setComparisons((prev) => prev.map((comparison) => (
          comparison.id === id ? { ...comparison, status: 'procesando' } : comparison
        )));
      } else {
        showAlert(t('disc.discComparisons.reprocessError'), 'error');
      }
    } catch (err) {
      console.error('Error reprocessing comparison:', err);
      showAlert(t('disc.discComparisons.reprocessError'), 'error');
    } finally {
      setReprocessingId(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      sent: 'info',
      started: 'warning',
      completed: 'success',
      processing: 'warning',
      evaluated: 'success',
      processing_error: 'error',
      expired: 'error',
      pendiente: 'warning',
      procesando: 'warning',
      completado: 'success',
      error: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      completado: t('disc.statusCompleted'),
      pendiente: t('disc.statusPending'),
      procesando: t('disc.statusProcessing'),
      error: t('disc.statusError'),
      completed: t('disc.statusCompleted'),
      pending: t('disc.statusPending'),
    };
    return labels[status] || status;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t('disc.notAvailable');
    const date = timestamp._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const canDeleteTest = () => {
    return ['super-admin', 'admin', 'admin-grafton'].includes(user?.rol);
  };

  const getAnalysisTypeLabel = (type) => {
    const types = {
      compareCandidates: t('disc.discComparisons.compareCandidates'),
      candidateVsBoss: t('disc.discComparisons.candidateVsBoss'),
      teamAnalysis: t('disc.discComparisons.teamAnalysis'),
    };
    return types[type] || type;
  };

  if (loadingComparisons) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorComparisons) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {errorComparisons}
      </Alert>
    );
  }

  if (comparisons.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No se encontraron comparaciones
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Crea tu primera comparación desde la pestaña de Comparaciones
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => fetchComparisons()}
        >
          {t('disc.refresh')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('disc.discComparisons.analysisType')}</TableCell>
              <TableCell>{t('disc.position')}</TableCell>
              <TableCell>{t('disc.testsCompared')}</TableCell>
              <TableCell>{t('disc.status')}</TableCell>
              <TableCell>{t('createdAt')}</TableCell>
              <TableCell>{t('disc.createdBy')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comparisons.map((comparison) => (
              <TableRow key={comparison.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {getAnalysisTypeLabel(comparison.analysis_type)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {comparison.position_name || t('disc.notAvailable')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {comparison.tests?.length || 0} {t('disc.tests')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(comparison.status)}
                    color={getStatusColor(comparison.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(comparison.created_at)}</TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {comparison.userEmail || t('disc.notAvailable')}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {/* Botón VER REPORTE */}
                  <Tooltip title={t('disc.viewReport')}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleViewComparison(comparison)}
                        disabled={comparison.status !== 'completado' || !comparison.reportUrl}
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Botón REPROCESAR - Solo super-admin, en comparaciones completadas o con error (no cobra puntos) */}
                  {isSuperAdmin && ['completado', 'error'].includes(comparison.status) && (
                    <Tooltip title={t('reprocess')}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setComparisonToReprocess(comparison)}
                          disabled={reprocessingId === comparison.id}
                          color="warning"
                          aria-label={t('reprocess')}
                        >
                          {reprocessingId === comparison.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <ReplayIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {/* Botón ELIMINAR */}
                  <Tooltip title={t('disc.deleteComparison')}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteComparisonDialog(comparison)}
                      color="error"
                      disabled={!canDeleteTest()}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal para ver comparaciones */}
      <Dialog
        open={comparisonModalOpen}
        onClose={() => setComparisonModalOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh',
            width: '95vw',
            maxWidth: '95vw',
            margin: 2
          }
        }}
      >
        <DialogContent sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          {selectedComparison && (
            <DiscComparisonResults
              comparisonId={selectedComparison.id}
              analysisType={selectedComparison.analysis_type}
              onBack={() => setComparisonModalOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de confirmación para eliminar comparación */}
      <Dialog
        open={deleteComparisonDialogOpen}
        onClose={handleCloseDeleteComparisonDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('disc.deleteComparisonTitle')}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('disc.deleteComparisonConfirm')}
          </Typography>
          {comparisonToDelete && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2">
                <strong>{t('disc.type')}:</strong> {getAnalysisTypeLabel(comparisonToDelete.analysis_type)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('disc.position')}:</strong> {comparisonToDelete.position_name || t('disc.notAvailable')}
              </Typography>
              <Typography variant="body2">
                <strong>{t('disc.status')}:</strong> {getStatusLabel(comparisonToDelete.status)}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error">
            {t('noWayBack')}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDeleteComparisonDialog}
            disabled={deletingComparison}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmDeleteComparison}
            color="error"
            variant="contained"
            disabled={deletingComparison}
          >
            {deletingComparison ? <CircularProgress size={20} color="inherit" /> : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación para reprocesar comparación (solo super-admin) */}
      <Dialog
        open={Boolean(comparisonToReprocess)}
        onClose={handleCloseReprocessDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('disc.discComparisons.reprocessTitle')}
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('disc.discComparisons.reprocessConfirm')}
          </Typography>
          {comparisonToReprocess && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t('disc.type')}:</strong> {getAnalysisTypeLabel(comparisonToReprocess.analysis_type)}
              </Typography>
              <Typography variant="body2">
                <strong>{t('disc.position')}:</strong> {comparisonToReprocess.position_name || t('disc.notAvailable')}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseReprocessDialog}>
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirmReprocess} color="warning" variant="contained">
            {t('reprocess')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiscDashboardComparison;